/**
 * Two-axis variant selection for the buy box.
 *
 * Shopify sells flavour x size as one variant, so the pair is what has to be
 * resolved. The two radio groups are the controls; the hidden `name="id"`
 * input is the only thing the form submits. Everything derives from the
 * selected pair, so the price, the availability and the gallery cannot
 * disagree with what is in the form.
 *
 * An option carrying a single value (Strength: 75 mg) has no group and no UI.
 */
(() => {
  const init = (root) => {
    const mapEl = root.querySelector('[data-notch-variant-map]');
    const idInput = root.querySelector('[data-notch-variant-input]');
    if (!mapEl || !idInput) return;

    let map;
    try { map = JSON.parse(mapEl.textContent); } catch { return; }
    if (!map || !Array.isArray(map.variants)) return;

    const radios = [...root.querySelectorAll('[data-notch-axis]')];
    if (!radios.length) return;

    const pick = (axis) =>
      radios.find((r) => r.dataset.notchAxis === axis && r.checked)?.value ?? null;

    const findVariant = () => {
      const flavour = pick('flavour');
      const size = pick('size');
      if (flavour === null || size === null) return null;
      return map.variants.find(
        (v) => v.options[map.flavourIndex] === flavour && v.options[map.sizeIndex] === size
      ) ?? null;
    };

    // Availability is per pair, so a size can be sold out in one flavour and
    // not another. Marking it on the label lets CSS grey it without the
    // control lying about what it will do.
    //
    // showStockState is false during pre-launch, when every variant is
    // deliberately unbuyable. Greying them all reads as "sold out", and a
    // sold-out product does not get clicked — which would leave the gate
    // behind the button collecting nothing. `available` in the map stays
    // truthful; this only governs whether the picker shows it.
    //
    // It is checked HERE rather than at init because this is where the class
    // and the disabled flag are set. Skipping earlier would leave a stale
    // --out on a re-render, which is the bug this section already had once.
    const markAvailability = () => {
      if (map.showStockState === false) return;
      const flavour = pick('flavour');
      radios
        .filter((r) => r.dataset.notchAxis === 'size')
        .forEach((r) => {
          const v = map.variants.find(
            (x) => x.options[map.flavourIndex] === flavour && x.options[map.sizeIndex] === r.value
          );
          const sold = !!v && !v.available;
          r.closest('.notch-buy__size')?.classList.toggle('notch-buy__size--out', sold);
          r.disabled = sold;
        });
    };

    // Only the selected flavour's shots. A slide or thumb carrying no flavour
    // belongs to no variant, so it is a shared scene and stays visible.
    const slides = [...root.querySelectorAll('.notch-buy__slide[data-flavour]')];
    const thumbs = [...root.querySelectorAll('.notch-buy__thumbs li[data-flavour]')];

    const filterGallery = () => {
      const flavour = pick('flavour');
      if (flavour === null) return;
      const keep = (el) => !el.dataset.flavour || el.dataset.flavour === flavour;
      slides.forEach((el) => { el.hidden = !keep(el); });
      thumbs.forEach((el) => { el.hidden = !keep(el); });
      // Back to the lead shot, so changing flavour never leaves you looking at
      // the third view of a pack you are no longer buying.
      const first = slides.find((el) => !el.hidden);
      if (first) first.scrollIntoView({ block: 'nearest', inline: 'start' });
    };

    // The purchase-options rows are rendered per variant and toggled, so the
    // price a shopper reads always belongs to the variant in the form. This
    // used to be nothing at all: the rows were rendered once from the
    // server's current variant and never touched, so changing size moved the
    // form to the 90 while the page went on showing the 72's price.
    //
    // The plan choice is carried across the swap by value. Without this, a
    // shopper who picked One-time and then changed size would be silently
    // moved back to Subscribe, because every pane checks its own first plan.
    const planPanes = [...root.querySelectorAll('[data-notch-plan-pane]')];

    const swapPlanPane = (variant) => {
      if (!planPanes.length) return;
      const live = planPanes.find((p) => !p.hidden);
      const keep = live
        ? live.querySelector('input[name="selling_plan"]:checked')?.value ?? null
        : null;

      planPanes.forEach((pane) => {
        const on = pane.dataset.notchPlanPane === String(variant.id);
        pane.hidden = !on;
        // Disabled, not just hidden: every pane carries a checked radio under
        // the same name, so a hidden pane left enabled would submit.
        pane.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          input.disabled = !on;
          if (on && keep !== null) input.checked = input.value === keep;
        });

        // Carrying the choice by value can match nothing: a variant Shopify
        // left out of the selling-plan group renders only the one-time row,
        // so a shopper arriving from Subscribe would land on a fieldset with
        // no option lit, submitting no `selling_plan` while the page showed
        // no choice at all. Worse, the next swap would read nothing back and
        // never recover. Fall back to the pane's own first row.
        if (on && !pane.querySelector('input[name="selling_plan"]:checked')) {
          const fallback = pane.querySelector('input[name="selling_plan"]');
          if (fallback) fallback.checked = true;
        }
      });
    };

    // The price lede under the title and the sticky bar both show "the plan
    // the page is on". They are server-rendered once, so this copies the
    // CHECKED row's title and figures into them whenever the row can change:
    // a plan change, a size swap (the panes are re-pointed), the one-time
    // deep link. The rows stay the single source; these are reflections.
    const syncLede = () => {
      const checked = root.querySelector('[data-notch-plan-pane]:not([hidden]) input[name="selling_plan"]:checked')
        ?? root.querySelector('input[name="selling_plan"]:checked:not([disabled])');
      const row = checked?.closest('.notch-buy__plan');
      if (!row) return;
      const title = row.querySelector('.notch-buy__plan-title')?.textContent.trim() ?? '';
      const price = row.querySelector('.notch-buy__price')?.textContent.trim() ?? '';
      const meta = row.querySelector('.notch-price__meta')?.textContent.trim().replace(/^·\s*/, '') ?? '';
      root.querySelectorAll('[data-notch-lede-label], [data-notch-sticky-label]').forEach((el) => { el.textContent = title; });
      root.querySelectorAll('[data-notch-lede-price], [data-notch-sticky-price]').forEach((el) => {
        const wrap = el.querySelector('.notch-price') ?? el;
        wrap.textContent = price;
        if (meta) {
          const m = document.createElement('span');
          m.className = 'notch-price__meta';
          m.textContent = ' · ' + meta;
          wrap.append(m);
        }
      });
    };
    root.addEventListener('change', (e) => { if (e.target?.name === 'selling_plan') syncLede(); });

    const apply = () => {
      const variant = findVariant();
      markAvailability();
      filterGallery();
      if (!variant) return;
      swapPlanPane(variant);
      syncLede();
      idInput.value = variant.id;
      // Keep the address bar honest, so a reload or a share reproduces the
      // same selection rather than the product's default.
      if (variant.url) {
        const url = new URL(variant.url, window.location.origin);
        window.history.replaceState({}, '', url.pathname + url.search);
      }
      root.dispatchEvent(
        new CustomEvent('notch:variant-change', { bubbles: true, detail: { variant } })
      );
    };

    radios.forEach((r) => r.addEventListener('change', apply));
    markAvailability();
    filterGallery();
    syncLede();
  };

  /**
   * A one-time deep link.
   *
   * Shopify's `selling_plan` parameter cannot express "no plan": an absent
   * one and an empty one both leave `selected_selling_plan` nil, so Liquid
   * renders the subscribe default for both and cannot tell them apart. An
   * EMPTY parameter is therefore the theme's own signal for one-time, and
   * this is the only place that can read it. The range tile's One-time
   * button links that way, so the choice made on the tile survives the trip.
   */
  const applyOneTime = (root) => {
    const raw = new URLSearchParams(window.location.search).get('selling_plan');
    if (raw !== '') return;
    // Scoped to the pane on show. Every variant renders its own rows now, so
    // an unscoped query returns the first pane's radio — usually a hidden,
    // disabled one — and checking that sets a plan on a variant nobody is
    // buying while the visible row still reads Subscribe.
    const once =
      root.querySelector(
        '[data-notch-plan-pane]:not([hidden]) input[name="selling_plan"][value=""]'
      ) ?? root.querySelector('input[name="selling_plan"][value=""]:not([disabled])');
    if (!once || once.checked) return;
    once.checked = true;
    once.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const boot = () =>
    document.querySelectorAll('[data-notch-buy-box]').forEach((root) => {
      init(root);
      applyOneTime(root);
    });

  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();
  // The theme editor tears a section down and rebuilds it; without this the
  // pickers stop working the moment a merchant edits the block.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll?.('[data-notch-buy-box]').forEach(init);
  });
})();
